'use client';
import React from 'react';
import Link from 'next/link';
import Button from '../../ui/Button';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { ChevronRight } from 'lucide-react';

const RollFormHero = () => {
    return (
        <section className="rpl-hero-section w-full relative flex items-center overflow-hidden bg-[#050a10]">
            {/* Optimized Background Image for LCP */}
            <div className="absolute inset-0 z-0">
                <Image
                    src="/images/premium_factory_roll_labels.png"
                    alt="Industrial Roll Form Label Manufacturing"
                    fill
                    priority
                    className="object-cover object-center"
                    sizes="100vw"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#050a10] via-[#050a10]/40 to-transparent"></div>
            </div>

            {/* Breadcrumb Wrapper for perfect vertical alignment */}
            <div className="rpl-hero-breadcrumb-wrapper">
                <div className="rpl-hero-breadcrumb-container">
                    <nav className="rpl-hero-breadcrumb">
                        <Link href="/" className="hover:text-white transition-colors">Home</Link>
                        <ChevronRight size={14} />
                        <Link href="/services" className="hover:text-white transition-colors">Our Services</Link>
                        <ChevronRight size={14} />
                        <span className="text-white">Roll Form Labels</span>
                    </nav>
                </div>
            </div>

            <div className="rpl-hero-frame">
                <div className="flex justify-start items-center w-full">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="rpl-hero-card"
                    >
                        {/* Ultra-Premium Glassmorphic Card */}
                        <div className="rpl-hero-card-inner rpl-service-card-inner group">
                            {/* Ambient highlight line */}
                            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

                            <div className="flex flex-col items-start w-full relative z-10 gap-fluid-lg">
                                <div className="flex flex-col items-start w-full gap-fluid-md">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4, duration: 0.8 }}
                                    >
                                        <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-white/80 type-meta">
                                            High-Volume Production
                                        </span>
                                    </motion.div>

                                    <motion.h1
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.6, duration: 0.8 }}
                                        className="rpl-hero-headline text-left w-full"
                                    >
                                        <span className="block font-serif italic text-white/90 mb-1 sm:mb-2">Industrial</span>
                                        <span className="block text-white">Scale Precision.</span>
                                    </motion.h1>

                                    <motion.p
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.8, duration: 0.8 }}
                                        className="rpl-hero-body text-left text-white/60"
                                    >
                                        Unmatched consistency and speed. Optimized for <span className="text-white font-medium">high-speed automated application</span> and global-scale manufacturing cycles.
                                    </motion.p>
                                </div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1, duration: 0.8 }}
                                    className="rpl-hero-buttons"
                                >
                                    <Link href="/quote" className="no-tap-enforce w-full sm:w-auto">
                                        <Button
                                            variant="primary"
                                            className="w-full sm:w-auto bg-white text-slate-950 rounded-full px-6 py-3 sm:px-8 sm:py-4 font-bold tracking-wider uppercase transition-all duration-300 hover:shadow-[0_20px_40px_rgba(255,255,255,0.15)] hover:-translate-y-1"
                                            style={{ fontSize: 'clamp(0.75rem, 0.9vw, 0.9375rem)' }}
                                        >
                                            Get a Quote
                                        </Button>
                                    </Link>
                                    <Link href="/contact" className="no-tap-enforce w-full sm:w-auto">
                                        <Button
                                            variant="outline"
                                            className="w-full sm:w-auto bg-transparent border-white/20 text-white hover:bg-white/5 rounded-full px-6 py-3 sm:px-8 sm:py-4 font-bold tracking-wider uppercase transition-all duration-300"
                                            style={{ fontSize: 'clamp(0.75rem, 0.9vw, 0.9375rem)' }}
                                        >
                                            View Capacity
                                        </Button>
                                    </Link>
                                </motion.div>
                            </div>
                        </div>

                        {/* Background light glow */}
                        <div className="absolute -z-10 -bottom-10 -left-10 w-64 h-64 bg-blue-600/10 rounded-full blur-[100px] animate-pulse"></div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default RollFormHero;
